import { UserRole, hasRequiredRole } from '@/types/auth'

export type PermissionExpr = UserRole | string | { and: PermissionExpr[] } | { or: PermissionExpr[] }

export interface PermissionSubject {
  role: string
  permissions: string[]
}

export function checkPermission(permissions: string[], required: string): boolean {
  if (!Array.isArray(permissions)) return false
  return permissions.includes('*') || permissions.includes(required)
}

export function evaluatePermission(expr: PermissionExpr, subject: PermissionSubject): boolean {
  if (typeof expr === 'string') {
    if ((Object.values(UserRole) as string[]).includes(expr)) {
      return hasRequiredRole(subject.role, expr as UserRole)
    }
    return checkPermission(subject.permissions, expr)
  }
  if ('and' in expr) return expr.and.every((e) => evaluatePermission(e, subject))
  if ('or' in expr) return expr.or.some((e) => evaluatePermission(e, subject))
  return false
}

/**
 * Gate de roteamento: quem gerencia canal default do phone OU árvore default de canal.
 * A tela `/admin/routing` foi removida — a config migrou pro detalhe do canal (`/admin/channels/[id]`).
 * Segue usado por `ADMIN_ACCESS` pra não trancar fora do /admin quem só gerencia roteamento.
 */
export const ROUTING_MANAGE: PermissionExpr = {
  or: ['channels:default:manage', 'channels:decision_tree:manage'],
}

/**
 * Gate das TELAS de admin (layout `/admin`): gestão de users OU de subroles OU de templates de canal OU
 * auditoria OU API keys OU árvores (flows) OU cache OU integrações OU config de envio em lote OU
 * roteamento OU mapas de roteamento OU perfil/números de telefone OU role dstorres. Precisa cobrir
 * os módulos do /admin (users, subroles, channels, audit, api-keys, decision-trees, phone-numbers,
 * cache, integrations, flow-maps, sending) — um gestor com grant só de um módulo não pode ficar
 * trancado fora do rail/nav só porque não tem `users:read`/`subroles:read`.
 * `settings:sending:manage` morde mais que os outros: é runtime-only (fora de todo preset), então
 * quem a recebe provavelmente NÃO tem nenhum outro grant de admin — sem ela aqui, a única pessoa a
 * quem concederam o acesso é justamente a que o `ProtectedRoute` manda de volta pro /chatbot.
 * `ROUTING_MANAGE` segue aqui mesmo sem tela própria: a config de roteamento vive no detalhe do canal.
 * `transmission_groups:read` NÃO entra: a tela é do atendente (`/chatbot/transmission-groups`, item
 * próprio no rail) e está no subrole default — mantê-la aqui jogava todo atendente num /admin vazio.
 * `admin:logs:read` entra pelo mesmo motivo de `settings:sending:manage`: runtime-only, fora de todo
 * preset — quem só tem esse grant ficaria trancado fora do /admin.
 * `settings:profile:read`/`:manage` e `channels:integration:read` (abas da `/admin/empresa`) entram pela
 * MESMA razão: runtime-only, fora de todo preset.
 * `security:policies:read` (faixa 28) entra pela MESMA razão: runtime-only, fora de todo preset.
 * `audit:team:read` (abas Equipe e Rede de `/admin/audit`, faixa 41 §1) entra pela MESMA razão: runtime-only, fora de
 * todo preset, e não implica `audit:read` — quem só a tem ficaria trancado fora do /admin (D32/D36).
 * O item "Admin" do RAIL NÃO usa esta expressão desde 2026-11-03: usa `ADMIN_HUB_ACCESS`
 * (`app/(private)/admin/_lib/hubAccess.ts`, OR dos cards do hub). `channels:read` segue aqui pro atendente abrir
 * `/admin/channels/{id}` pelo "Vincular listas" sem ver "Admin" no rail (o hub dele seria vazio).
 */
export const ADMIN_ACCESS: PermissionExpr = {
  or: [
    'users:read',
    'subroles:read',
    'channels:templates:manage',
    'channels:read',
    'channels:phones:read', // Telefones: o hub/nav gateiam pelo que o GET exige, não por channels:read
    'channels:account:read', // Conta WhatsApp
    'audit:read',
    'audit:team:read', // Auditoria › Equipe e Rede — runtime-only, mesma razão de settings:sending:manage
    'admin:logs:read', // Erros HTTP: runtime-only, mesma razão de settings:sending:manage
    'api_keys:manage',
    'flows:read',
    'flow_maps:read',
    'admin:cache:manage',
    'integrations:manage',
    'settings:sending:manage',
    'settings:profile:read', // Empresa › Perfil — runtime-only, mesma razão de settings:sending:manage
    'settings:profile:manage', // Empresa › Perfil (escrita) — idem
    'channels:integration:read', // Empresa › Integração — fora de todo preset
    'security:policies:read', // Políticas de acesso (Empresa › Segurança, subrole, user) — runtime-only, idem
    ROUTING_MANAGE,
    UserRole.DSTORRES,
  ],
}
